import * as Sentry from '@sentry/browser';
import { find, last, map } from 'lodash';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import moment from 'moment';

import epaxios from '@/utils/epaxios';
import { checkTierPermission, waitFor } from '@/utils/util-functions';
import { ComponentTierName } from '@/constants/enums';
import { EnergyEfficiencyYearlyViewMock } from '@/constants/EnergyEfficiencyYearlyViewMock';
import { token } from '@/theme';

import RootStore from './RootStore';

export default class EnergySavingsGraphStore {
  rootStore: typeof RootStore;
  @observable energySavingsGraphs;
  @observable isLoading = false;
  @observable isReady = false;
  @observable lastPage = 0;
  @observable baselineData;
  @observable yearlyViewOpen = false;

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action setYearlyViewOpen = newValue => {
    this.yearlyViewOpen = newValue;
  };

  @action get = async () => {
    try {
      this.resetGraph();
      this.isLoading = true;
      const stationId = this.getStationId();
      let data;
      await waitFor(() => this.rootStore.stationStore.isLoading === false);

      if (checkTierPermission(ComponentTierName.EnergySavingsWidget, this.rootStore.stationStore)) {
        data = EnergyEfficiencyYearlyViewMock;
      } else {
        data = (await epaxios.get(`value-dashboard/energy-saving/station/${stationId}/yearly-view`))?.data;
      }
      if (data?.validationErrorName) {
        throw new Error(data.msg);
      }
      runInAction(() => {
        this.energySavingsGraphs = new YearlyEnergySavingsGraph(data);
        this.lastPage = data.yearOffsetLimit;
        this.isReady = true;
      });
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Energy Saving Yearly View: ${e}`));
    } finally {
      this.isLoading = false;
    }
  };

  @action getGraphByYear = async year => {
    try {
      let endpointEnding = '';
      this.isLoading = true;
      const stationId = this.getStationId();
      if (year < moment().year()) {
        endpointEnding = `/${moment().year() - year}`;
      }

      const { data } = await epaxios.get(
        `value-dashboard/energy-saving/station/${stationId}/yearly-view${endpointEnding}`,
      );
      if (data?.validationErrorName) {
        throw new Error(data.msg);
      }
      runInAction(() => {
        this.energySavingsGraphs = new YearlyEnergySavingsGraph(data);
        this.isReady = true;
      });
    } catch (e) {
      this.energySavingsGraphs = undefined;
    } finally {
      this.isLoading = false;
    }
  };

  getStationId() {
    return this.rootStore.stationStore?.currentStation.id;
  }

  @action getBaselinePeriods = async () => {
    try {
      const stationId = this.getStationId();
      const { data } = await epaxios.get(`/station/${stationId}/baseline-info`);
      this.baselineData = last(data.baselinePeriods);
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Baseline Periods: ${e}`));
    }
  };

  @action resetGraphOnYearChange = () => {
    this.energySavingsGraphs = undefined;
  };

  @action resetGraph = () => {
    this.energySavingsGraphs = undefined;
    this.isLoading = false;
    this.isReady = false;
    this.lastPage = 0;
    this.baselineData = undefined;
  };
}

class YearlyEnergySavingsGraph {
  @observable startDate;
  @observable endDate;
  @observable unitOfMeasure;
  @observable year;
  @observable monthsWithInMaintenance;
  @observable data = [];

  constructor(energySavingGraph) {
    makeObservable(this);
    this.startDate = energySavingGraph.startDate;
    this.endDate = energySavingGraph.endDate;
    this.unitOfMeasure = energySavingGraph.unitOfMeasure;
    this.monthsWithInMaintenance = energySavingGraph.monthsWithInMaintenance;
    this.year = moment(this.startDate).year();
    this.data = map(energySavingGraph.data, data => new Data(data));
    this.monthsWithInMaintenance = map(
      energySavingGraph.monthsWithInMaintenance,
      month => new MonthWithInMaintenance(month),
    );
  }

  @computed get baselineSeries() {
    const baselineSeries = find(this.data, data => data.parameterName === 'baselineNormalizedEnergyKwh')?.paramData;
    const actualEnergySeries = find(this.data, data => data.parameterName === 'energyKwh')?.paramData;

    const baselineSeriesWithLabels = map(baselineSeries, (point, i) => {
      const baselineData = find(this.data, data => data.parameterName === 'baselineNormalizedEnergyKwh')?.series;
      if (
        find(this.monthsWithInMaintenance, month => month.monthNumber === baselineData[i]?.monthNumber) &&
        point.y > actualEnergySeries[i]?.y
      ) {
        point.isInMaintenance = true;
      }

      const newPoint = {
        ...point,
        volume: this.volumeSeries[i]?.y,
        specificPower: this.baselineSpecificPowerSeries[i + 1]?.y,
      };

      return newPoint;
    });

    return baselineSeriesWithLabels;
  }

  @computed get actualEnergySeries() {
    const actualEnergySeries = find(this.data, data => data.parameterName === 'energyKwh')?.paramData;
    const baselineSeries = find(this.data, data => data.parameterName === 'baselineNormalizedEnergyKwh')?.paramData;
    const co2Series = find(this.data, { parameterName: 'co2MetricTons' })?.paramData;

    const actualEnergySeriesWithColorsAndLabels = map(actualEnergySeries, (point, i) => {
      const actualEnergyData = find(this.data, data => data.parameterName === 'energyKwh')?.series;
      // (BaselineSP / ActualSP - 1) * currentCO2 -> CO2 reduction compared to baseline co2
      const co2Saving = (baselineSeries[i].y / point.y - 1) * co2Series?.[i].y;
      if (point.y >= baselineSeries[i]?.y) {
        const monthWithInMaintenance = find(this.monthsWithInMaintenance, {
          monthNumber: actualEnergyData[i]?.monthNumber,
        });
        point.color = token.colorInfo;

        if (monthWithInMaintenance) {
          point.isInMaintenance = true;
        }
      }

      const energySaving = baselineSeries[i].y - point.y;
      const newPoint = {
        ...point,
        volume: this.volumeSeries[i].y,
        specificPower: this.specificPowerSeries[i].y,
        baseline: this.baselineSeries[i].y,
        energySaving: energySaving,
        co2Saving: co2Saving,
      };

      return newPoint;
    });

    return actualEnergySeriesWithColorsAndLabels;
  }

  @computed get volumeSeries() {
    return find(this.data, { parameterName: 'volumeM3' })?.paramData;
  }

  @computed get specificPowerSeries() {
    const baselineSeries = find(this.data, data => data.parameterName === 'baselineSpecificPower')?.paramData;
    const specificPowerSeries = find(this.data, data => data.parameterName === 'specificPower')?.paramData;
    const actualEnergySeries = find(this.data, data => data.parameterName === 'energyKwh')?.paramData;

    const specificPowerWithPointValues = map(specificPowerSeries, (point, i) => {
      const energySaving = baselineSeries[i].y - point.y;
      const specificPowerData = find(this.data, data => data.parameterName === 'specificPower')?.series;
      const monthWithInMaintenance = find(this.monthsWithInMaintenance, {
        monthNumber: specificPowerData[i]?.monthNumber,
      });

      const newPoint = {
        ...point,
        volume: this.volumeSeries[i].y,
        actualEnergy: actualEnergySeries[i].y,
        energySaving: energySaving,
      };

      if (monthWithInMaintenance) {
        newPoint.isInMaintenance = true;
      }
      return newPoint;
    });

    return specificPowerWithPointValues;
  }

  @computed get baselineSpecificPowerSeries() {
    const series = find(this.data, { parameterName: 'baselineSpecificPower' })?.paramData;
    const value = series ? series[0].y : null;
    const newSeries = map(
      Array.from({ length: 14 }, (_, i) => i),
      x => ({ x, y: value }),
    );

    return newSeries;
  }

  @computed get energySavingSeries() {
    const actualEnergy = this.actualEnergySeries;
    const baseline = this.baselineSeries;
    const savingSeries = [];

    baseline.forEach((point, i) => {
      savingSeries.push({
        x: point.x,
        y: point.y - actualEnergy[i]?.y,
      });
    });

    return savingSeries;
  }
}

class Data {
  @observable parameterName;
  @observable series = [];

  constructor(parametersData) {
    makeObservable(this);
    this.parameterName = parametersData.parameter;
    this.series = map(parametersData.series, point => new Point(point));
  }

  @computed get paramData() {
    return map(this.series, point => {
      return {
        x: point.monthNumber,
        y: point.value,
      };
    });
  }
}

class Point {
  @observable timestamp;
  @observable value;

  constructor(monthData) {
    makeObservable(this);
    this.timestamp = monthData[0];
    this.value = monthData[1];
  }

  @computed get monthNumber() {
    return Number(moment(this.timestamp).utc().format('MM'));
  }

  @computed get monthName() {
    return moment(this.timestamp).utc().format('MMM');
  }
}

class MonthWithInMaintenance {
  @observable timestamp;

  constructor(timestamp) {
    makeObservable(this);
    this.timestamp = timestamp;
  }

  @computed get monthNumber() {
    return Number(moment(this.timestamp).utc().format('MM'));
  }

  @computed get monthName() {
    return moment(this.timestamp).utc().format('MMM');
  }
}
